// Static medical content (informational only — not medical advice).
// Used by hub pages, the home services grid and the medications detail page.
// Disease list is the canonical 3 conditions: diabetes, hypertension, dyslipidemia.

export const DISEASES = [
  {
    slug: "diabetes",
    name: "Diabetes Mellitus",
    short: "A chronic condition affecting how your body turns food into energy.",
    image: "https://images.pexels.com/photos/18870282/pexels-photo-18870282.jpeg?auto=compress&cs=tinysrgb&w=1200",
  },
  {
    slug: "dyslipidemia",
    name: "High Cholesterol Levels",
    short: "An imbalance in blood cholesterol and triglycerides.",
    image: "https://images.pexels.com/photos/19438563/pexels-photo-19438563.jpeg?auto=compress&cs=tinysrgb&w=1200",
  },
  {
    slug: "hypertension",
    name: "Hypertension",
    short: "Persistently elevated blood pressure that silently strains the heart.",
    image: "https://images.pexels.com/photos/14558560/pexels-photo-14558560.jpeg?auto=compress&cs=tinysrgb&w=1200",
  },
];

// Conditions surfaced in the booking form's dropdown.
export const BOOKING_CONDITIONS = [
  "Diabetes",
  "Hypertension",
  "High Cholesterol Levels",
  "Multiple (combination of diabetes, hypertension, cholesterol)",
  "General consultation",
];

export const MEDICATIONS = {
  diabetes: [
    {
      name: "Metformin",
      class: "Biguanide",
      use: "First-line oral agent that lowers liver glucose output and improves insulin sensitivity.",
      notes: "Take with meals to reduce GI upset; monitor B12 long-term.",
    },
    {
      name: "GLP-1 Agonists (e.g., Semaglutide, Liraglutide)",
      class: "Incretin mimetic",
      use: "Lowers post-meal sugar, slows gastric emptying, supports weight loss and reduces cardiovascular risk.",
      notes: "Weekly or daily injection. Start low and titrate.",
    },
    {
      name: "SGLT-2 Inhibitors (e.g., Empagliflozin, Dapagliflozin)",
      class: "Glucose excretion",
      use: "Excretes excess glucose via urine, protects kidneys and heart.",
      notes: "Stay well hydrated; watch for genital infections.",
    },
    {
      name: "DPP-4 Inhibitors (e.g., Sitagliptin)",
      class: "Incretin enhancer",
      use: "Modest sugar lowering with low hypoglycaemia risk.",
      notes: "Generally well tolerated.",
    },
    {
      name: "Insulin",
      class: "Hormone replacement",
      use: "Used when oral agents are insufficient or in type 1 diabetes.",
      notes: "Titrated based on glucose trends; risk of hypoglycaemia.",
    },
  ],
  hypertension: [
    {
      name: "ACE Inhibitors (e.g., Ramipril, Enalapril)",
      class: "RAAS blocker",
      use: "Lowers BP, protects kidneys especially in diabetes.",
      notes: "May cause a dry cough; monitor potassium and creatinine.",
    },
    {
      name: "ARBs (e.g., Telmisartan, Losartan)",
      class: "Angiotensin receptor blocker",
      use: "Alternative to ACEi without the cough.",
      notes: "Once-daily dosing; effective in CKD and diabetes.",
    },
    {
      name: "Calcium Channel Blockers (e.g., Amlodipine)",
      class: "CCB",
      use: "Relaxes blood vessels; effective in elderly and isolated systolic hypertension.",
      notes: "May cause ankle swelling.",
    },
    {
      name: "Thiazide Diuretics (e.g., Chlorthalidone, Indapamide)",
      class: "Diuretic",
      use: "Reduces fluid volume; effective combination agent.",
      notes: "Monitor sodium, potassium and uric acid.",
    },
    {
      name: "Beta Blockers (e.g., Metoprolol, Bisoprolol)",
      class: "Beta-adrenergic blocker",
      use: "Used when angina, heart failure, or arrhythmia coexist.",
      notes: "Not first-line for uncomplicated hypertension.",
    },
  ],
  dyslipidemia: [
    {
      name: "Statins (e.g., Atorvastatin, Rosuvastatin)",
      class: "HMG-CoA reductase inhibitor",
      use: "First-line for lowering LDL and reducing cardiovascular events.",
      notes: "Monitor liver enzymes and muscle symptoms.",
    },
    {
      name: "Ezetimibe",
      class: "Cholesterol absorption inhibitor",
      use: "Add-on therapy when statins alone don't achieve LDL targets.",
      notes: "Combined with statin in fixed-dose tablets.",
    },
    {
      name: "PCSK9 Inhibitors (e.g., Evolocumab)",
      class: "Monoclonal antibody",
      use: "For very high risk or familial hypercholesterolemia.",
      notes: "Subcutaneous injection every 2–4 weeks.",
    },
    {
      name: "Fibrates (e.g., Fenofibrate)",
      class: "PPAR-α activator",
      use: "Primarily for high triglycerides.",
      notes: "Use cautiously with statins.",
    },
    {
      name: "Omega-3 Fatty Acids",
      class: "Lipid modulator",
      use: "Adjunct for elevated triglycerides.",
      notes: "Use prescription-grade formulations where possible.",
    },
  ],
};

// Returns the human-friendly disease name for a slug.
export const diseaseName = (slug) => {
  const d = DISEASES.find((x) => x.slug === slug);
  return d ? d.name : slug;
};

export const DIET_VIDEOS = [
  { title: "Plate Method for Diabetes", youtubeId: "FsXc8Mhf6BE" },
  { title: "DASH Diet for Blood Pressure", youtubeId: "5NaWHfPTAQE" },
  { title: "Healthy Fats Explained", youtubeId: "VQOhFSan-Yc" },
  { title: "Reading Nutrition Labels", youtubeId: "ZQzqDb9JpFQ" },
];

export const EXERCISE_VIDEOS = [
  { title: "20-Minute Beginner Walk Workout", youtubeId: "njeZ29umqVE" },
  { title: "Gentle Strength for Seniors", youtubeId: "v7AYKMP6rOE" },
  { title: "Yoga for Beginners", youtubeId: "LqXZ628YNj4" },
  { title: "Low-Impact Resistance Routine", youtubeId: "ml6cT4AZdqI" },
];
